import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse, HttpParams } from '@angular/common/http';
import { Observable, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  Dossier,
  Upload,
  Document,
  ExtractionResult,
  DossierCheck,
  DossierAlert,
  DiscrepancyKpi,
  DiscrepancyCatalogEntry,
  SignatureStatus,
  BatchSignatureStatus,
} from '../models/dossier.model';

/** Per-document outcome of a downstream export. */
export interface DownstreamExportItem {
  document_id: string;
  document_type?: string;
  sent: boolean;
  status_code?: number;
  response?: string;
  reason?: string;
}

/** Response shape of the downstream export endpoints. */
export interface DownstreamExportResult {
  system_name?: string | null;
  documents_sent: number;
  documents_total: number;
  results: DownstreamExportItem[];
}

/**
 * HTTP client for all /dossier/* endpoints.
 *
 * Every call includes `withCredentials: true` so the session cookie
 * is forwarded to the API (same pattern as AuthService).
 */
@Injectable({ providedIn: 'root' })
export class DossierService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiBaseUrl}/dossier`;

  // --- Dossier CRUD ---

  createDossier(
    externalId: string,
    processingMode: string,
    batchMode: boolean = false,
    batchDocumentType: string | null = null,
    batchModel: string | null = null,
    batchOutputFormat: string | null = null,
  ): Observable<Dossier> {
    return this.http.post<Dossier>(
      this.apiUrl,
      {
        external_id: externalId,
        processing_mode: processingMode,
        batch_mode: batchMode,
        batch_document_type: batchDocumentType || null,
        batch_model: batchModel || null,
        batch_output_format: batchOutputFormat || null,
      },
      { withCredentials: true },
    );
  }

  listDossiers(opts: {
    ref?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: 'created_at' | 'external_id' | 'status';
    sortDir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  } = {}): Observable<{ items: Dossier[]; total: number }> {
    let params = new HttpParams();
    if (opts.ref) params = params.set('ref', opts.ref);
    if (opts.status) params = params.set('status', opts.status);
    if (opts.dateFrom) params = params.set('date_from', opts.dateFrom);
    if (opts.dateTo) params = params.set('date_to', opts.dateTo);
    if (opts.sortBy) params = params.set('sort_by', opts.sortBy);
    if (opts.sortDir) params = params.set('sort_dir', opts.sortDir);
    params = params.set('limit', String(opts.limit ?? 50));
    params = params.set('offset', String(opts.offset ?? 0));
    return this.http.get<{ items: Dossier[]; total: number }>(
      this.apiUrl, { params, withCredentials: true },
    );
  }

  getDossier(dossierId: string): Observable<Dossier> {
    return this.http.get<Dossier>(`${this.apiUrl}/${dossierId}`, {
      withCredentials: true,
    });
  }

  /** Update the batch_model setting on an existing dossier. */
  updateBatchModel(dossierId: string, batchModel: string | null): Observable<Dossier> {
    return this.http.patch<Dossier>(
      `${this.apiUrl}/${dossierId}/batch-model`,
      { batch_model: batchModel },
      { withCredentials: true },
    );
  }

  /** Update the batch_output_format setting on an existing dossier. */
  updateBatchOutputFormat(dossierId: string, batchOutputFormat: string | null): Observable<Dossier> {
    return this.http.patch<Dossier>(
      `${this.apiUrl}/${dossierId}/batch-output-format`,
      { batch_output_format: batchOutputFormat },
      { withCredentials: true },
    );
  }

  /** Update the external_id (customer reference / downstream <ref>) on a dossier. */
  updateExternalId(dossierId: string, externalId: string): Observable<Dossier> {
    return this.http.patch<Dossier>(
      `${this.apiUrl}/${dossierId}/external-id`,
      { external_id: externalId },
      { withCredentials: true },
    );
  }

  /** Toggle the trade_finance flag on a dossier. */
  updateTradeFinanceFlag(dossierId: string, tradeFinance: boolean): Observable<Dossier> {
    return this.http.patch<Dossier>(
      `${this.apiUrl}/${dossierId}/trade-finance`,
      { trade_finance: tradeFinance },
      { withCredentials: true },
    );
  }

  /** Update the tradefinance downstream-header fields on a dossier. */
  updateTradefinanceHeaders(
    dossierId: string,
    headers: { customer_key: string | null; account_key: string | null; from_country: string | null },
  ): Observable<Dossier> {
    return this.http.patch<Dossier>(
      `${this.apiUrl}/${dossierId}/tradefinance-headers`,
      headers,
      { withCredentials: true },
    );
  }


  deleteDossier(dossierId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${dossierId}`, {
      withCredentials: true,
    });
  }

  // --- File upload ---

  uploadFile(dossierId: string, file: File): Observable<Upload> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<Upload>(
      `${this.apiUrl}/${dossierId}/upload`,
      formData,
      { withCredentials: true },
    );
  }

  uploadFileBatch(dossierId: string, files: File[]): Observable<Upload> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    return this.http.post<Upload>(
      `${this.apiUrl}/${dossierId}/upload-batch`,
      formData,
      { withCredentials: true },
    );
  }

  deleteUpload(dossierId: string, uploadId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}`,
      { withCredentials: true },
    );
  }

  /** Signature-verification status for an upload's original file(s) — one
   *  result for a single PDF, one per file for a batch/ZIP upload. */
  getUploadSignature(
    dossierId: string, uploadId: string,
  ): Observable<SignatureStatus | BatchSignatureStatus> {
    return this.http.get<SignatureStatus | BatchSignatureStatus>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/signature`,
      { withCredentials: true },
    );
  }

  /** Re-run verification against the trusted lists as they stand now, and
   *  replace the stored snapshot — Acrobat's "Validate Signature". Needs the
   *  dossier's DEK unlocked, since the originals are encrypted at rest. */
  reverifyUploadSignature(
    dossierId: string, uploadId: string,
  ): Observable<SignatureStatus | BatchSignatureStatus> {
    return this.http.post<SignatureStatus | BatchSignatureStatus>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/signature/verify`,
      {},
      { withCredentials: true },
    );
  }

  // --- Documents ---

  getDocuments(dossierId: string, uploadId: string): Observable<Document[]> {
    return this.http.get<Document[]>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents`,
      { withCredentials: true },
    );
  }

  updateDocumentType(
    dossierId: string,
    uploadId: string,
    documentId: string,
    documentType: string,
  ): Observable<Document> {
    return this.http.put<Document>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}`,
      { document_type: documentType },
      { withCredentials: true },
    );
  }

  deleteDocument(
    dossierId: string,
    uploadId: string,
    documentId: string,
    mode: 'merge' | 'ignore' = 'merge',
  ): Observable<Document> {
    return this.http.delete<Document>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}?mode=${mode}`,
      { withCredentials: true },
    );
  }

  /** Mark a document (group of pages) as IGNORE — excluded from extraction/examination. */
  ignoreDocument(
    dossierId: string,
    uploadId: string,
    documentId: string,
  ): Observable<Document> {
    return this.http.post<Document>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/ignore`,
      {},
      { withCredentials: true },
    );
  }

  /** Ignore (or un-ignore) a single page — one click, reversible; OCR excluded from extraction. */
  ignorePage(
    dossierId: string,
    uploadId: string,
    documentId: string,
    pageId: string,
    ignored: boolean,
  ): Observable<{ page: any; document_ignored: boolean }> {
    return this.http.patch<{ page: any; document_ignored: boolean }>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/pages/${pageId}/ignore`,
      { ignored },
      { withCredentials: true },
    );
  }

  // --- Extraction ---

  triggerExtraction(dossierId: string, uploadId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/extract`,
      {},
      { withCredentials: true },
    );
  }

  getExtraction(
    dossierId: string,
    uploadId: string,
    documentId: string,
  ): Observable<ExtractionResult> {
    return this.http.get<ExtractionResult>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/extraction`,
      { withCredentials: true },
    );
  }

  updateExtractionField(
    dossierId: string,
    uploadId: string,
    documentId: string,
    fieldPath: string,
    value: unknown,
  ): Observable<ExtractionResult> {
    return this.http.put<ExtractionResult>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/extraction`,
      { field_path: fieldPath, value },
      { withCredentials: true },
    );
  }

  saveExtraction(
    dossierId: string,
    uploadId: string,
    documentId: string,
    extractionResult: Record<string, unknown>,
  ): Observable<ExtractionResult> {
    return this.http.post<ExtractionResult>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/extraction/save`,
      { extraction_result: extractionResult },
      { withCredentials: true },
    );
  }

  validateExtraction(
    dossierId: string,
    uploadId: string,
    documentId: string,
    extractionResult: Record<string, unknown>,
  ): Observable<{ valid: boolean | null; errors: string | null }> {
    return this.http.post<{ valid: boolean | null; errors: string | null }>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/extraction/validate`,
      { extraction_result: extractionResult },
      { withCredentials: true },
    );
  }

  markReviewed(
    dossierId: string,
    uploadId: string,
    documentId: string,
  ): Observable<Document> {
    return this.http.post<Document>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/review`,
      {},
      { withCredentials: true },
    );
  }

  cancelExtraction(
    dossierId: string,
    uploadId: string,
    documentId: string,
  ): Observable<Document> {
    return this.http.post<Document>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/cancel-extraction`,
      {},
      { withCredentials: true },
    );
  }

  // --- Results ---

  getResults(dossierId: string): Observable<Record<string, unknown>> {
    return this.http.get<Record<string, unknown>>(
      `${this.apiUrl}/${dossierId}/results`,
      { withCredentials: true },
    );
  }

  downloadResults(
    dossierId: string,
    uploadId: string,
    documentOids: string[],
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/results/download`,
      { document_oids: documentOids },
      { withCredentials: true, responseType: 'blob', observe: 'response' },
    );
  }

  // --- Page image ---

  getPageImage(
    dossierId: string,
    uploadId: string,
    pageId: string,
  ): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${dossierId}/uploads/${uploadId}/pages/${pageId}/image`, {
      withCredentials: true,
      responseType: 'blob',
    });
  }

  getPageOcr(
    dossierId: string,
    uploadId: string,
    pageId: string,
  ): Observable<any> {
    return this.http.get(`${this.apiUrl}/${dossierId}/uploads/${uploadId}/pages/${pageId}/ocr`, {
      withCredentials: true,
    });
  }

  // --- Page / document manipulation ---

  reorderPage(
    dossierId: string,
    uploadId: string,
    documentId: string,
    pageOid: string,
    newPosition: number,
  ): Observable<Document[]> {
    return this.http.post<Document[]>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/pages/reorder`,
      { page_oid: pageOid, new_position: newPosition },
      { withCredentials: true },
    );
  }

  movePage(
    dossierId: string,
    uploadId: string,
    documentId: string,
    pageId: string,
    direction: 'up' | 'down',
  ): Observable<Document[]> {
    return this.http.post<Document[]>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/pages/${pageId}/move`,
      { direction },
      { withCredentials: true },
    );
  }

  splitDocument(
    dossierId: string,
    uploadId: string,
    documentId: string,
    pageOid: string,
  ): Observable<Document[]> {
    return this.http.post<Document[]>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/split`,
      { page_oid: pageOid },
      { withCredentials: true },
    );
  }

  mergeDocumentNext(
    dossierId: string,
    uploadId: string,
    documentId: string,
  ): Observable<Document[]> {
    return this.http.post<Document[]>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/merge-next`,
      {},
      { withCredentials: true },
    );
  }

  // --- Document types ---

  getDocumentTypes(): Observable<{ internal: string[]; external: string[]; output_formats?: Record<string, string[]> }> {
    const baseUrl = environment.apiBaseUrl;
    return this.http.get<{ internal: string[]; external: string[]; output_formats?: Record<string, string[]> }>(
      `${baseUrl}/document_types`,
      { withCredentials: true },
    );
  }

  // --- Config ---

  /** Fetch runtime configuration (defaults, timeouts, billing). */
  getConfig(): Observable<Record<string, any>> {
    return this.http.get<Record<string, any>>(
      `${environment.apiBaseUrl}/config`,
      { withCredentials: true },
    );
  }

  // --- Polling ---

  /**
   * Polls extraction status every 5 seconds.
   * Caller should unsubscribe or use `takeUntil` / `takeWhile`
   * when the document reaches 'extracted' or a terminal status.
   */
  pollExtractionStatus(
    dossierId: string,
    uploadId: string,
    documentId: string,
  ): Observable<ExtractionResult> {
    return interval(5000).pipe(
      switchMap(() => this.getExtraction(dossierId, uploadId, documentId)),
    );
  }

  // --- Models ---

  /** Fetch available extraction models from the simple-flow /models endpoint. */
  getModels(): Observable<{ models: string[] }> {
    return this.http.get<{ models: string[] }>(
      `${environment.apiBaseUrl}/models`,
      { withCredentials: true },
    );
  }

  // --- Re-extract ---

  /** Re-extract a single document with a specified model and document type. */
  reExtract(
    dossierId: string,
    uploadId: string,
    documentId: string,
    model: string,
    documentType: string,
    outputFormat: string = 'json',
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/re-extract`,
      { model, document_type: documentType, output_format: outputFormat },
      { withCredentials: true },
    );
  }

  // --- Document originality ---

  /** Mark a document as original, copy, or unassessed. */
  updateDocumentOriginal(
    dossierId: string,
    uploadId: string,
    documentId: string,
    isOriginal: boolean | null,
  ): Observable<Document> {
    return this.http.patch<Document>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/original`,
      { is_original: isOriginal },
      { withCredentials: true },
    );
  }

  /** Bulk-set originality for all documents in a dossier (IGNORE pages skipped). */
  flagAllOriginal(
    dossierId: string,
    isOriginal: boolean | null,
  ): Observable<{ updated: number; is_original: boolean | null }> {
    return this.http.patch<{ updated: number; is_original: boolean | null }>(
      `${this.apiUrl}/${dossierId}/documents/original-bulk`,
      { is_original: isOriginal },
      { withCredentials: true },
    );
  }

  // --- Dossier-level verification checks ---

  /** Get all verification checks for a dossier (seeds from template on first call). */
  getDossierChecks(dossierId: string): Observable<DossierCheck[]> {
    return this.http.get<DossierCheck[]>(
      `${this.apiUrl}/${dossierId}/checks`,
      { withCredentials: true },
    );
  }

  /** Update a single verification check. */
  updateDossierCheck(
    dossierId: string,
    checkId: string,
    status: string,
    notes: string | null,
  ): Observable<DossierCheck> {
    return this.http.patch<DossierCheck>(
      `${this.apiUrl}/${dossierId}/checks/${checkId}`,
      { status, notes },
      { withCredentials: true },
    );
  }

  /** Run LLM-based auto-verification of UCP-600 checks. */
  autoVerifyChecks(
    dossierId: string,
    model: string | null = null,
  ): Observable<{ checks: DossierCheck[]; usage: { input: number; output: number }; model: string; auto_verified: number; total_checks: number }> {
    return this.http.post<any>(
      `${this.apiUrl}/${dossierId}/checks/auto-verify`,
      { model },
      { withCredentials: true },
    );
  }

  // --- Dossier discrepancies (alerts) ---

  /** Get all discrepancies (engine/auto/manual) for a dossier. */
  getDossierAlerts(dossierId: string): Observable<DossierAlert[]> {
    return this.http.get<DossierAlert[]>(
      `${this.apiUrl}/${dossierId}/alerts`,
      { withCredentials: true },
    );
  }

  /** Add a manual (user-entered) discrepancy. */
  createDossierAlert(
    dossierId: string,
    payload: { description: string; rule_name?: string | null; doc_type?: string | null; field?: string | null; value?: string | null },
  ): Observable<DossierAlert> {
    return this.http.post<DossierAlert>(
      `${this.apiUrl}/${dossierId}/alerts`,
      payload,
      { withCredentials: true },
    );
  }

  /** Update a discrepancy's status. */
  updateDossierAlert(
    dossierId: string,
    alertId: string,
    status: string,
    notes: string | null,
  ): Observable<DossierAlert> {
    return this.http.patch<DossierAlert>(
      `${this.apiUrl}/${dossierId}/alerts/${alertId}`,
      { status, notes },
      { withCredentials: true },
    );
  }

  /** Run cross-document discrepancy detection (engine). */
  detectDiscrepancies(dossierId: string): Observable<{ alerts: DossierAlert[] } | any> {
    return this.http.post<any>(
      `${this.apiUrl}/${dossierId}/detect-discrepancies`,
      {},
      { withCredentials: true },
    );
  }

  /** Get the auto-vs-manual discrepancy KPI. */
  getDiscrepancyKpi(dossierId: string): Observable<DiscrepancyKpi> {
    return this.http.get<DiscrepancyKpi>(
      `${this.apiUrl}/${dossierId}/alerts/kpi`,
      { withCredentials: true },
    );
  }

  /** Get the UCP-ISBP discrepancy catalog (for manual-add selection). */
  getDiscrepancyCatalog(): Observable<DiscrepancyCatalogEntry[]> {
    return this.http.get<DiscrepancyCatalogEntry[]>(
      `${this.apiUrl}/discrepancy-catalog`,
      { withCredentials: true },
    );
  }

  getDownstreamEndpoint(): Observable<{ endpoint_url: string | null; system_name: string | null }> {
    return this.http.get<{ endpoint_url: string | null; system_name: string | null }>(
      `${this.apiUrl}/my-org/downstream-endpoint`,
      { withCredentials: true },
    );
  }

  exportToDownstream(dossierId: string): Observable<DownstreamExportResult> {
    return this.http.post<DownstreamExportResult>(
      `${this.apiUrl}/${dossierId}/export/downstream`,
      {},
      { withCredentials: true },
    );
  }

  exportUploadToDownstream(dossierId: string, uploadId: string): Observable<DownstreamExportResult> {
    return this.http.post<DownstreamExportResult>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/export/downstream`,
      {},
      { withCredentials: true },
    );
  }

  exportDocumentToDownstream(
    dossierId: string, uploadId: string, documentId: string,
  ): Observable<DownstreamExportResult> {
    return this.http.post<DownstreamExportResult>(
      `${this.apiUrl}/${dossierId}/uploads/${uploadId}/documents/${documentId}/export/downstream`,
      {},
      { withCredentials: true },
    );
  }
}

// ---------------------------------------------------------------------------
// Agent Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class AgentService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiBaseUrl}/agent`;

  chatHelp(message: string, sessionId?: string): Observable<{ response: string; session_id: string }> {
    return this.http.post<{ response: string; session_id: string }>(
      `${this.apiUrl}/help/chat`,
      { message, session_id: sessionId },
      { withCredentials: true },
    );
  }

  chatOps(message: string, sessionId?: string): Observable<{ response: string; session_id: string }> {
    return this.http.post<{ response: string; session_id: string }>(
      `${this.apiUrl}/ops/chat`,
      { message, session_id: sessionId },
      { withCredentials: true },
    );
  }

  chatTfCompliance(message: string, dossierId: string, sessionId?: string): Observable<{ response: string; session_id: string }> {
    return this.http.post<{ response: string; session_id: string }>(
      `${this.apiUrl}/tf-compliance/chat`,
      { message, session_id: sessionId, dossier_id: dossierId },
      { withCredentials: true },
    );
  }

  tfComplianceStatus(): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(`${this.apiUrl}/tf-compliance/status`, { withCredentials: true });
  }

  helpStatus(): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(`${this.apiUrl}/help/status`, { withCredentials: true });
  }

  opsStatus(): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(`${this.apiUrl}/ops/status`, { withCredentials: true });
  }
}
